import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Filter, X, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { FilterPresetManager } from './FilterPresetManager';
import {
  LeadFilters,
  ENTRY_DATE_PRESETS,
  CLOSURE_DATE_PRESETS,
  LAST_CONTACT_PRESETS,
  parseDateFromInput,
  formatDateForInput
} from '@/types/filters';
import { User } from '@/lib/api';

interface AdvancedFiltersProps {
  filters: LeadFilters;
  onFiltersChange: (filters: LeadFilters) => void;
  onApply: () => void;
  allUsers: User[];
  userId: string; // Current user ID for preset storage
  className?: string;
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  filters,
  onFiltersChange,
  onApply,
  allUsers,
  userId,
  className
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState<LeadFilters>(filters);

  // Sync local filters with external filters
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Constants for filter options
  const STAGES = [
    { value: 'NEW', label: 'New' },
    { value: 'CONTACTED', label: 'Contacted' },
    { value: 'DISCUSSION', label: 'Discussion' },
    { value: 'PROPOSAL', label: 'Proposal' },
    { value: 'NEGOTIATION', label: 'Negotiation' },
    { value: 'WON', label: 'Won' },
    { value: 'LOST', label: 'Lost' }
  ];

  const PRIORITIES = [
    { value: 'HOT', label: 'Hot', color: 'text-red-600 bg-red-50' },
    { value: 'WARM', label: 'Warm', color: 'text-orange-600 bg-orange-50' },
    { value: 'COLD', label: 'Cold', color: 'text-blue-600 bg-blue-50' }
  ];

  const RISK_LEVELS = [
    { value: 'NONE', label: 'None' },
    { value: 'LOW', label: 'Low' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HIGH', label: 'High' }
  ];

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.entryDateFrom || localFilters.entryDateTo) count++;
    if (localFilters.closureDateFrom || localFilters.closureDateTo) count++;
    if (localFilters.lastContactFrom || localFilters.lastContactTo) count++;
    if (localFilters.stages && localFilters.stages.length > 0) count++;
    if (localFilters.priorities && localFilters.priorities.length > 0) count++;
    if (localFilters.assignedTo && localFilters.assignedTo.length > 0) count++;
    if (localFilters.dealValueMin !== undefined || localFilters.dealValueMax !== undefined) count++;
    if (localFilters.riskLevel) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  // Handle filter changes
  const handleMultiSelectChange = (field: 'stages' | 'priorities' | 'assignedTo', value: string) => {
    const currentValues = localFilters[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];

    setLocalFilters({ ...localFilters, [field]: newValues.length > 0 ? newValues : undefined });
  };

  const handleDateRangeChange = (
    field: 'entryDate' | 'closureDate' | 'lastContact',
    from: Date | null,
    to: Date | null
  ) => {
    const fromField = `${field}From` as keyof LeadFilters;
    const toField = `${field}To` as keyof LeadFilters;

    setLocalFilters({
      ...localFilters,
      [fromField]: from ? formatDateForInput(from) : undefined,
      [toField]: to ? formatDateForInput(to) : undefined
    });
  };

  const handleDealValueChange = (type: 'min' | 'max', value: string) => {
    const numValue = value ? parseFloat(value) : undefined;
    setLocalFilters({
      ...localFilters,
      [type === 'min' ? 'dealValueMin' : 'dealValueMax']: numValue
    });
  };

  const handleRiskLevelChange = (value: string) => {
    setLocalFilters({
      ...localFilters,
      riskLevel: value as LeadFilters['riskLevel']
    });
  };

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApply();
  };

  const handleClearAll = () => {
    const emptyFilters: LeadFilters = {
      stages: undefined,
      priorities: undefined,
      assignedTo: undefined,
      dealValueMin: undefined,
      dealValueMax: undefined,
      riskLevel: undefined,
      entryDateFrom: undefined,
      entryDateTo: undefined,
      closureDateFrom: undefined,
      closureDateTo: undefined,
      lastContactFrom: undefined,
      lastContactTo: undefined
    };
    setLocalFilters(emptyFilters);
    onFiltersChange(emptyFilters);
    onApply();
  };

  const handleLoadPreset = (presetFilters: LeadFilters) => {
    setLocalFilters(presetFilters);
    onFiltersChange(presetFilters);
    onApply();
  };

  // Remove individual filter chip
  const removeFilter = (filterKey: keyof LeadFilters) => {
    const newFilters = { ...localFilters };

    if (filterKey === 'entryDateFrom' || filterKey === 'entryDateTo') {
      newFilters.entryDateFrom = undefined;
      newFilters.entryDateTo = undefined;
    } else if (filterKey === 'closureDateFrom' || filterKey === 'closureDateTo') {
      newFilters.closureDateFrom = undefined;
      newFilters.closureDateTo = undefined;
    } else if (filterKey === 'lastContactFrom' || filterKey === 'lastContactTo') {
      newFilters.lastContactFrom = undefined;
      newFilters.lastContactTo = undefined;
    } else if (filterKey === 'dealValueMin' || filterKey === 'dealValueMax') {
      newFilters.dealValueMin = undefined;
      newFilters.dealValueMax = undefined;
    } else {
      newFilters[filterKey] = undefined;
    }

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
    onApply();
  };

  // Render active filter chips
  const renderFilterChips = () => {
    const chips: JSX.Element[] = [];

    if (localFilters.entryDateFrom || localFilters.entryDateTo) {
      chips.push(
        <div key="entryDate" className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
          <span>Entry: {localFilters.entryDateFrom || '...'} - {localFilters.entryDateTo || '...'}</span>
          <button onClick={() => removeFilter('entryDateFrom')} className="hover:bg-blue-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (localFilters.closureDateFrom || localFilters.closureDateTo) {
      chips.push(
        <div key="closureDate" className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
          <span>Closure: {localFilters.closureDateFrom || '...'} - {localFilters.closureDateTo || '...'}</span>
          <button onClick={() => removeFilter('closureDateFrom')} className="hover:bg-green-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (localFilters.lastContactFrom || localFilters.lastContactTo) {
      chips.push(
        <div key="lastContact" className="flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
          <span>Contact: {localFilters.lastContactFrom || '...'} - {localFilters.lastContactTo || '...'}</span>
          <button onClick={() => removeFilter('lastContactFrom')} className="hover:bg-purple-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (localFilters.stages && localFilters.stages.length > 0) {
      chips.push(
        <div key="stages" className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
          <span>Stages: {localFilters.stages.join(', ')}</span>
          <button onClick={() => removeFilter('stages')} className="hover:bg-blue-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (localFilters.priorities && localFilters.priorities.length > 0) {
      chips.push(
        <div key="priorities" className="flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
          <span>Priorities: {localFilters.priorities.join(', ')}</span>
          <button onClick={() => removeFilter('priorities')} className="hover:bg-orange-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (localFilters.assignedTo && localFilters.assignedTo.length > 0) {
      chips.push(
        <div key="assignedTo" className="flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full">
          <span>Assigned: {localFilters.assignedTo.length} user(s)</span>
          <button onClick={() => removeFilter('assignedTo')} className="hover:bg-indigo-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (localFilters.dealValueMin !== undefined || localFilters.dealValueMax !== undefined) {
      chips.push(
        <div key="dealValue" className="flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
          <span>Value: {localFilters.dealValueMin || '0'} - {localFilters.dealValueMax || '∞'}</span>
          <button onClick={() => removeFilter('dealValueMin')} className="hover:bg-yellow-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (localFilters.riskLevel) {
      chips.push(
        <div key="riskLevel" className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
          <span>Risk: {localFilters.riskLevel}</span>
          <button onClick={() => removeFilter('riskLevel')} className="hover:bg-red-200 rounded-full p-0.5">
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    return chips;
  };

  return (
    <div className={cn('bg-gray-50 border border-gray-200 rounded-lg', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <Filter className="w-4 h-4" />
          Advanced Filters
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full">
              {activeFilterCount}
            </span>
          )}
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        <div className="flex items-center gap-2">
          <FilterPresetManager
            currentFilters={localFilters}
            onLoadPreset={handleLoadPreset}
            userId={userId}
          />
        </div>
      </div>

      {/* Filter Chips (Always Visible) */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-4 bg-white border-b border-gray-200">
          {renderFilterChips()}
          <button
            onClick={handleClearAll}
            className="text-xs text-gray-600 hover:text-gray-900 underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Expanded Filter Panel */}
      {isExpanded && (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date Range Filters */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Date Filters</h3>

              <DateRangePicker
                label="Lead Entry Date"
                from={localFilters.entryDateFrom ? parseDateFromInput(localFilters.entryDateFrom) || undefined : undefined}
                to={localFilters.entryDateTo ? parseDateFromInput(localFilters.entryDateTo) || undefined : undefined}
                onChange={(from, to) => handleDateRangeChange('entryDate', from, to)}
                presets={ENTRY_DATE_PRESETS}
              />

              <DateRangePicker
                label="Expected Closure Date"
                from={localFilters.closureDateFrom ? parseDateFromInput(localFilters.closureDateFrom) || undefined : undefined}
                to={localFilters.closureDateTo ? parseDateFromInput(localFilters.closureDateTo) || undefined : undefined}
                onChange={(from, to) => handleDateRangeChange('closureDate', from, to)}
                presets={CLOSURE_DATE_PRESETS}
              />

              <DateRangePicker
                label="Last Contact Date"
                from={localFilters.lastContactFrom ? parseDateFromInput(localFilters.lastContactFrom) || undefined : undefined}
                to={localFilters.lastContactTo ? parseDateFromInput(localFilters.lastContactTo) || undefined : undefined}
                onChange={(from, to) => handleDateRangeChange('lastContact', from, to)}
                presets={LAST_CONTACT_PRESETS}
              />
            </div>

            {/* Other Filters */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Other Filters</h3>

              {/* Stage Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pipeline Stage
                </label>
                <div className="flex flex-wrap gap-2">
                  {STAGES.map((stage) => (
                    <button
                      key={stage.value}
                      type="button"
                      onClick={() => handleMultiSelectChange('stages', stage.value)}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                        localFilters.stages?.includes(stage.value)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      )}
                    >
                      {stage.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority Level
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRIORITIES.map((priority) => (
                    <button
                      key={priority.value}
                      type="button"
                      onClick={() => handleMultiSelectChange('priorities', priority.value)}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                        localFilters.priorities?.includes(priority.value)
                          ? priority.color
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      )}
                    >
                      {priority.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assigned To Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assigned To
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2 bg-white">
                  {allUsers.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={localFilters.assignedTo?.includes(user.id) || false}
                        onChange={() => handleMultiSelectChange('assignedTo', user.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-900">{user.name}</span>
                      <span className="text-xs text-gray-500">({user.role})</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Deal Value Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deal Value Range
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={localFilters.dealValueMin || ''}
                    onChange={(e) => handleDealValueChange('min', e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={localFilters.dealValueMax || ''}
                    onChange={(e) => handleDealValueChange('max', e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Risk Level Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Risk Level
                </label>
                <div className="flex flex-wrap gap-2">
                  {RISK_LEVELS.map((risk) => (
                    <button
                      key={risk.value}
                      type="button"
                      onClick={() => handleRiskLevelChange(risk.value)}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium rounded-md border transition-colors',
                        localFilters.riskLevel === risk.value
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      )}
                    >
                      {risk.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleClearAll}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Clear All
            </button>
            <button
              onClick={handleApply}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
